import React, {FC, useEffect, useState} from 'react'
import {KTIcon} from '../../../../helpers'
import {useLayout} from '../../../../layout/core'
import moment from 'moment'
import {CryptoTransactionType} from '../../../../../app/pages/transactions/CryptoTransactions'

const DrawerCryptoTransactionDetails: FC = () => {
  const {transactionHistoryDetails} = useLayout()
  const [txnDetails, setTxnDetails] = useState<CryptoTransactionType | null>(null)

  useEffect(() => {
    if (transactionHistoryDetails) {
      setTxnDetails(transactionHistoryDetails)
    }
  }, [transactionHistoryDetails])

  return (
    <div
      id='crypto-transaction-history-drawer'
      className='bg-body'
      data-kt-drawer='true'
      data-kt-drawer-name='crypto-transaction-history'
      data-kt-drawer-activate='true'
      data-kt-drawer-overlay='true'
      data-kt-drawer-width="{default:'300px', 'lg': '600px'}"
      data-kt-drawer-direction='end'
      data-kt-drawer-toggle='#crypto-transaction-history-drawer-toggle'
      data-kt-drawer-close='#crypto-transaction-history-close'
    >
      <div className='card rounded-0 w-100'>
        {/* Header */}
        <div className='card-header'>
          <h3 className='card-title fw-bolder text-dark'>Crypto Transaction Details</h3>
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-active-light-primary me-n5'
              id='crypto-transaction-history-close'
            >
              <KTIcon iconName='cross' className='fs-1' />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className='card-body bg-light'>
          {!txnDetails ? (
            <div className='text-center py-10'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-column gap-5'>
              <div className='d-flex justify-content-between border-bottom pb-3'>
                <span className='fw-bold text-gray-600'>Transaction ID:</span>
                <span className='fw-semibold text-dark'>
                  {txnDetails.trade_id || 'No ID found'}
                </span>
              </div>

              <div className='d-flex justify-content-between border-bottom pb-3'>
                <span className='fw-bold text-gray-600'>Type:</span>
                <span className='fw-semibold text-capitalize badge badge-light-primary text-primary'>
                  {txnDetails.category?.replace(/_/g, ' ')}
                </span>
              </div>

              {txnDetails.category == 'Send' || 'on-chain_transfer' ? (
                <>
                  <div className='d-flex justify-content-between border-bottom pb-3'>
                    <span className='fw-bold text-gray-600'>Sender PayAiro Tag:</span>
                    <span className='fw-semibold text-dark'>{txnDetails.sender}</span>
                  </div>
                  <div className='d-flex justify-content-between border-bottom pb-3'>
                    <span className='fw-bold text-gray-600'>Sender Email:</span>
                    <span className='fw-semibold text-dark'>{txnDetails.user_email}</span>
                  </div>

                  <div className='d-flex justify-content-between border-bottom pb-3'>
                    <span className='fw-bold text-gray-600'>Receiver PayAiro Tag:</span>
                    <span className='fw-semibold text-dark'>{txnDetails.recipient}</span>
                  </div>
                  <div className='d-flex justify-content-between border-bottom pb-3'>
                    <span className='fw-bold text-gray-600'>Receiver Email:</span>
                    <span className='fw-semibold text-dark'>{txnDetails.to_user_email}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className='d-flex justify-content-between border-bottom pb-3'>
                    <span className='fw-bold text-gray-600'>User PayAiro Tag:</span>
                    <span className='fw-semibold text-dark'>{txnDetails.sender}</span>
                  </div>

                  <div className='d-flex justify-content-between border-bottom pb-3'>
                    <span className='fw-bold text-gray-600'>User Email:</span>
                    <span className='fw-semibold text-dark'>{txnDetails.user_email}</span>
                  </div>
                  {txnDetails.withdrawal_address && (
                    <div className='d-flex justify-content-between border-bottom pb-3'>
                      <span className='fw-bold text-gray-600'>Withdrawal Address:</span>
                      <span className='fw-semibold text-dark'>{txnDetails.withdrawal_address}</span>
                    </div>
                  )}
                </>
              )}

              <div className='d-flex justify-content-between border-bottom pb-3'>
                <span className='fw-bold text-gray-600'>From Currency:</span>
                <span className='fw-semibold text-dark'>{txnDetails.from_currency}</span>
              </div>
              <div className='d-flex justify-content-between border-bottom pb-3'>
                <span className='fw-bold text-gray-600'>To Currency:</span>
                <span className='fw-semibold text-dark'>{txnDetails.to_currency}</span>
              </div>
              <div className='d-flex justify-content-between border-bottom pb-3'>
                <span className='fw-bold text-gray-600'>Currency Icon:</span>
                <span className='fw-semibold text-dark'>
                  <img src={txnDetails.icon} alt='icon' className='h-20px h-lg-30px ' />
                </span>
              </div>

              <div className='d-flex justify-content-between border-bottom pb-3'>
                <span className='fw-bold text-gray-600'>Amount:</span>
                <span className='fw-semibold text-dark'>${txnDetails.amount}</span>
              </div>

              <div className='d-flex justify-content-between border-bottom pb-3'>
                <span className='fw-bold text-gray-600'>Transaction Fees:</span>
                <span className='fw-semibold text-dark'>${txnDetails.fees || 0}</span>
              </div>

              <div className='d-flex justify-content-between border-bottom pb-3'>
                <span className='fw-bold text-gray-600'>Status:</span>
                <span
                  className={`badge text-capitalize fw-semibold ${
                    txnDetails.status == 'complete' || txnDetails.status == 'success'
                      ? 'badge-light-success'
                      : txnDetails.status == 'pending'
                      ? 'badge-light-warning'
                      : 'badge-light-danger'
                  }`}
                >
                  {txnDetails.status}
                </span>
              </div>

              <div className='d-flex justify-content-between border-bottom pb-3'>
                <span className='fw-bold text-gray-600'>Date:</span>
                <div className='d-flex flex-column align-items-end'>
                  <span className='fw-semibold text-dark'>
                    {moment(txnDetails.timestamp).format('DD MMM YYYY')}
                  </span>
                  <span className='fw-semibold text-dark'>
                    {moment(txnDetails.timestamp).format('hh:mm A')}
                  </span>
                </div>
              </div>

              {/* <div className='d-flex flex-column'>
                                <span className='fw-bold text-gray-600 mb-1'>Description:</span>
                                <span className='fw-semibold text-dark'>{txnDetails.note}</span>
                            </div> */}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export {DrawerCryptoTransactionDetails}
